import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type ShopDTO, type WbCardSyncStateDTO, type WbCardSyncTaskDTO } from './api';
import './shop-card-sync.css';

type SyncResult = WbCardSyncStateDTO['result'];
type Bubble = { kind: 'busy' } | { kind: 'success'; result: NonNullable<SyncResult> } | { kind: 'error'; message: string };
type View = { scope: string; busy: boolean; bubble: Bubble | null };

function validCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function checkedState(value: WbCardSyncStateDTO): WbCardSyncStateDTO {
  if (!value || typeof value.busy !== 'boolean' || value.task === undefined) throw new Error('Incomplete sync state');
  const task = value.task;
  if (task && (typeof task.id !== 'string' || typeof task.shopId !== 'string' || !['pending', 'running', 'complete', 'failed', 'expired'].includes(task.status))) throw new Error('Incomplete sync task');
  const result = value.result;
  return { ...value, result: result && validCount(result.dailyLimit) && validCount(result.dailyRemaining)
    ? { ...result, totalRemaining: validCount(result.totalRemaining) ? result.totalRemaining : null } : null };
}

function failureMessage(code: string | null): string {
  if (code?.includes('CREDENTIAL') || code?.includes('TOKEN') || code?.includes('UNAUTHORIZED') || /^WB_HTTP_(401|403)$/u.test(code??'')) return '获取失败，请更换有效的 WB Token 并保存，再点击刷新按钮重试。';
  if (code?.includes('EXPIRED') || code?.includes('DEADLINE')) return '获取额度超时，请稍后点击刷新按钮重试。';
  return '获取店铺额度失败，请稍后点击店铺左下角刷新按钮重试。';
}

/** The server owns the durable task and global gate; this hook only observes it. */
export function useShopCardSync(scopeKey: string, onComplete: (task: WbCardSyncTaskDTO, result: SyncResult) => void) {
  const [view, setView] = useState<View>({ scope: scopeKey, busy: false, bubble: null });
  const scope = useRef(scopeKey);
  scope.current = scopeKey;
  const completed = useRef(onComplete);
  completed.current = onComplete;
  const startRef = useRef<(shopId: string) => void>(() => {});
  const closeRef = useRef<() => void>(() => {});

  useEffect(() => {
    let active = true;
    let revision = 0;
    let locked = false;
    let trackedTaskId: string | null = null;
    let trackedQueryId: string | null = null;
    let trackedShopId: string | null = null;
    let observedTask = false;
    let readFailures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let successTimer: ReturnType<typeof setTimeout> | undefined;
    let current: View = { scope: scopeKey, busy: false, bubble: null };
    const finished = new Set<string>();
    const isCurrent = () => active && scope.current === scopeKey;
    const publish = (next: View) => { if (isCurrent()) { current = next; setView(next); } };
    const clearPoll = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined; };
    const clearSuccess = () => { if (successTimer !== undefined) clearTimeout(successTimer); successTimer = undefined; };
    const schedule = (delay: number, source: 'initial' | 'poll' = 'poll') => { clearPoll(); timer = setTimeout(() => { void read(source); }, delay); };

    function accept(raw: WbCardSyncStateDTO, source: 'initial' | 'post' | 'poll', requestedTaskId?: string | null): void {
      const data = checkedState(raw);
      readFailures = 0;
      const task = data.task;
      const running = task?.status === 'pending' || task?.status === 'running';
      let bubble = current.bubble;
      if (task && ((source === 'initial' && running && !observedTask) || source === 'post')) {
        trackedTaskId = task.id;
        trackedQueryId = task.id;
        trackedShopId = task.shopId;
        observedTask = true;
        if (running) bubble = { kind: 'busy' };
      }
      // A cursor recovery may replace the run with a child. The requested run
      // still identifies our task chain, even when the global gate has moved on.
      if (task && requestedTaskId && requestedTaskId === trackedQueryId && task.shopId === trackedShopId) trackedTaskId = task.id;
      if (task && task.id === trackedTaskId && !running) {
        trackedTaskId = null;
        trackedQueryId = null;
        trackedShopId = null;
        if (!finished.has(task.id)) {
          finished.add(task.id);
          if (task.status === 'complete') {
            completed.current(task, data.result);
            bubble = data.result && data.result.totalRemaining !== null
              ? { kind: 'success', result: data.result }
              : { kind: 'error', message: '校准已结束，部分额度暂未返回。已保留已有数值，可稍后点击刷新按钮重试。' };
            if (bubble.kind === 'success') {
              clearSuccess();
              successTimer = setTimeout(() => { if (isCurrent()) publish({ ...current, bubble: null }); }, 6000);
            }
          } else {
            bubble = { kind: 'error', message: failureMessage(task.errorCode ?? (task.status === 'expired' ? 'EXPIRED' : null)) };
          }
        }
      }
      locked = data.busy || Boolean(trackedTaskId && running);
      if (!locked && bubble?.kind === 'busy') bubble = null;
      publish({ ...current, busy: locked, bubble });
      if (locked) schedule(2500);
      else clearPoll();
    }

    async function read(source: 'initial' | 'poll'): Promise<void> {
      const request = revision;
      const requestedTaskId = trackedQueryId;
      try {
        const data = await api.getShopCardSyncState(requestedTaskId ?? undefined);
        if (!isCurrent() || request !== revision) return;
        accept(data, source, requestedTaskId);
      } catch {
        if (!isCurrent() || request !== revision) return;
        readFailures++;
        if (readFailures < 3) schedule(2500 * 2 ** (readFailures - 1), source);
        else {
          // A failed status read must not leave the browser permanently locked.
          // Retrying POST is safe: server admission is global and idempotent.
          locked = false;
          publish({ ...current, busy: false, bubble: { kind: 'error', message: '额度状态暂时读取失败，后台任务仍会继续。请稍后点击刷新按钮重试。' } });
        }
      }
    }

    startRef.current = (shopId) => {
      if (!isCurrent() || locked) return;
      locked = true; // Guard the second click before React flushes its state update.
      const request = ++revision;
      trackedTaskId = null;
      trackedQueryId = null;
      trackedShopId = null;
      observedTask = false;
      readFailures = 0;
      clearPoll(); clearSuccess();
      publish({ ...current, busy: true, bubble: { kind: 'busy' } });
      void api.syncShopCardLimits(shopId).then((data) => {
        if (isCurrent() && request === revision) accept(data, 'post');
      }).catch((reason: unknown) => {
        if (!isCurrent() || request !== revision) return;
        if (reason instanceof ApiError && reason.code === 'WB_CARD_SYNC_BUSY') {
          void read('initial');
          return;
        }
        locked = false;
        publish({ ...current, busy: false, bubble: { kind: 'error', message: reason instanceof ApiError && reason.code?.includes('CREDENTIAL')
          ? failureMessage(reason.code)
          : reason instanceof ApiError && reason.code === 'WB_CARD_SYNC_SHOP_BLOCKED'
          ? '该店铺已被阻止，请先核对店铺状态或更新有效的 WB Token。'
          : reason instanceof ApiError && reason.code === 'SHOP_MODE_UNSUPPORTED'
          ? '该店铺或当前运行模式不支持真实 WB 额度同步。'
          : reason instanceof ApiError && (reason.status === 401 || reason.status === 403)
          ? '当前账号或凭证无法获取额度，请检查登录和已保存的 WB Token 后重试。'
          : '获取请求暂未确认，请稍后点击刷新按钮重试；已受理的后台任务会继续。' } });
      });
    };
    closeRef.current = () => { clearSuccess(); publish({ ...current, bubble: null }); };
    publish(current);
    void read('initial');
    return () => { active = false; revision++; clearPoll(); clearSuccess(); };
  }, [scopeKey]);

  const visible = view.scope === scopeKey ? view : { scope: scopeKey, busy: false, bubble: null };
  return { busy: visible.busy, start: (shopId: string) => startRef.current(shopId), bubble: <ShopCardSyncBubble bubble={visible.bubble} onClose={() => closeRef.current()} /> };
}

function ShopCardSyncBubble({ bubble, onClose }: { bubble: Bubble | null; onClose: () => void }) {
  if (!bubble) return null;
  return <div className="shop-card-sync-overlay"><div className={`shop-card-sync-bubble shop-card-sync-${bubble.kind}`} role="status" aria-live="polite">
    <button type="button" className="shop-card-sync-close" aria-label="关闭额度提示" onClick={onClose}>×</button>
    {bubble.kind === 'busy' ? <><span className="shop-card-sync-spinner" aria-hidden="true" /><strong>获取店铺额度中</strong><span className="shop-card-sync-help">可继续操作或离开页面，后台会继续获取。</span></>
      : bubble.kind === 'success' ? <><strong>店铺额度已更新</strong><span className="shop-card-sync-value">{bubble.result.totalRemaining} / {bubble.result.dailyLimit}</span><span className="shop-card-sync-help">总剩余 / 日限额</span></>
        : <><strong>店铺额度提示</strong><span className="shop-card-sync-help">{bubble.message}</span></>}
  </div></div>;
}

export function ShopCardSyncButton({ shop, busy, onSync }: { shop: ShopDTO; busy: boolean; onSync: () => void }) {
  const unavailable = shop.mode !== 'real' ? '模拟店铺无需获取 WB 额度' : shop.status === 'blocked' ? '请先核对店铺状态或更新有效 Token' : shop.credentialStatus !== 'configured' ? '请先保存有效的 WB Token，再获取额度' : null;
  return <button type="button" className="button shop-card-sync-button" aria-label={`刷新 ${shop.name} 的 WB 额度`} title={busy ? '已在同步中' : unavailable ?? '从 WB 重新获取此店铺的总剩余和日限额'} disabled={busy || unavailable !== null} onClick={onSync}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 7a7 7 0 0 1 11.6-1L20 9M4 15l2.3 3A7 7 0 0 0 17.9 17" /></svg>
  </button>;
}
